import { useSearchSession } from './SearchSessionContext';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { recentSearches, useRecentSearches } from './recentSearches';
import '../../styles/AppParity.css';
import { useSearchEditing } from './SearchEditingContext';
import './SearchAssist.css';
import { Search, Close } from '@mui/icons-material';
import { useMediaQuery } from '@mui/material';
import baseline, { appCopy, appTextStyle } from '../../app-ui/baseline';
import '../../pages/search/SearchResultsApp.css';

export default function SearchEntry({
  initialValue = '',
  showHistory = false,
  compact = false,
  assist = false,
  home = false,
  headerLabel,
}) {
  const { t, locale } = useWebLocale();
  const wideHeader = useMediaQuery('(min-width: 768px)');
  const placeholder =
    headerLabel && wideHeader
      ? headerLabel
      : compact
        ? appCopy(locale, 'search.placeholder')
        : t('searchHint');
  const session = useSearchSession();
  const inputRef = useRef(null);
  const composing = useRef(false);
  const [localText, setLocalText] = useState(initialValue);
  const editing = useSearchEditing();
  const enabled = assist && Boolean(editing);
  const text = enabled ? (editing.active ? editing.text : initialValue) : localText;
  const setText = (value) => {
    setLocalText(value);
    if (enabled) editing.edit(value, composing.current);
  };
  const recent = useRecentSearches();
  const navigate = useNavigate();
  useEffect(() => setLocalText(initialValue), [initialValue]);
  const submit = (value, context = 'search_submit') => {
    const query = value.trim();
    if (!query || composing.current) return;
    session?.submit(query, context);
    recentSearches.add(query);
    if (enabled) editing.close();
    inputRef.current?.blur();
    navigate(`/search/summary?q=${encodeURIComponent(query)}`);
  };
  return (
    <div className={`pp-parity${enabled ? ' pp-search-editing-entry' : ''}`}>
      <form
        className={`pp-search-form${compact ? ' pp-search-form-compact' : ''}${home && !editing?.active ? ' pp-search-form-idle' : ''}`}
        role="search"
        style={
          compact
            ? {
                height:
                  home && !editing?.active
                    ? baseline.search.result.searchField.height
                    : baseline.search.result.resultSearchField.height,
                borderRadius: baseline.search.result.searchField.borderRadius,
              }
            : undefined
        }
        onSubmit={(event) => {
          event.preventDefault();
          if (!composing.current && !event.nativeEvent.isComposing) submit(text);
        }}
      >
        {compact && (
          <button
            type="submit"
            className="pp-search-submit-icon"
            aria-label={t('search')}
            disabled={!text.trim()}
          >
            <Search aria-hidden="true" />
          </button>
        )}
        <input
          ref={inputRef}
          style={
            compact
              ? {
                  ...appTextStyle(baseline.tokens.typography.body),
                  lineHeight: '20px',
                }
              : undefined
          }
          onCompositionStart={() => {
            composing.current = true;
            if (enabled) editing.edit(text, true);
          }}
          onCompositionEnd={() => {
            composing.current = false;
            if (enabled) editing.edit(inputRef.current.value, false);
          }}
          type={compact ? 'text' : 'search'}
          role="searchbox"
          inputMode="search"
          enterKeyHint="search"
          autoCapitalize="none"
          spellCheck={false}
          autoComplete="off"
          onFocus={() => {
            if (enabled && !editing.active) editing.edit(text);
          }}
          aria-label={headerLabel && wideHeader ? headerLabel : t('searchHint')}
          placeholder={placeholder}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (
              event.key === 'Enter' &&
              (composing.current || event.nativeEvent.isComposing || event.keyCode === 229)
            )
              event.preventDefault();
          }}
        />
        {compact ? (
          text && (
            <button
              type="button"
              className="pp-search-clear"
              aria-label={appCopy(locale, 'search.clearQuery')}
              onClick={() => {
                session?.clear();
                setText('');
                inputRef.current?.focus();
              }}
            >
              <Close aria-hidden="true" />
            </button>
          )
        ) : (
          <button className="pp-action" type="submit" disabled={!text.trim()}>
            {t('search')}
          </button>
        )}
      </form>
      {enabled && editing.active && (
        <button
          type="button"
          className="pp-search-cancel"
          onClick={() => {
            session?.clear();
            editing.close();
            setLocalText('');
            inputRef.current?.blur();
            navigate('/search');
          }}
        >
          {appCopy(locale, 'common.cancel')}
        </button>
      )}
      {showHistory && recent.length > 0 && (
        <section className="pp-discovery-section" aria-label={t('recentSearches')}>
          <div className="pp-section-heading">
            <h2>{t('recentSearches')}</h2>
            <button className="pp-action" onClick={() => recentSearches.clear()}>
              {t('clearAll')}
            </button>
          </div>
          <ul className="pp-recent-searches">
            {recent.map((value) => (
              <li key={value}>
                <button onClick={() => submit(value, 'recent_search')}>{value}</button>
                <button
                  aria-label={`${t('removeSearch')}: ${value}`}
                  onClick={() => recentSearches.remove(value)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
